/**
 * Kontakty hub — hívásnapló (Volanie gomb, audit alapján)
 */

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { apiFetch } from "@/lib/api-client";
import { ContactsHubShell } from "@/components/contacts/ContactsHubShell";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { AppEmptyState } from "@/components/ui/app-empty-state";
import type { CallingCallLogsListResponse } from "@shared/api";
import {
  CallLogsDailyChartCard,
  type CallLogChartFilterState,
} from "@/components/calling/CallLogsDailyChartCard";
import { useAuthLite } from "@/context/AuthLite";
import { cn } from "@/lib/utils";

const LIST_PAGE_SIZE = 50;

function callingDealPath(dealId: string) {
  return `/crm/deals/${dealId}?tab=superfaktura`;
}

export default function ContactsCallLog() {
  const { t } = useTranslation();
  const { user } = useAuthLite();

  const [chartFilters, setChartFilters] = useState<CallLogChartFilterState>({
    scope: "me",
    range: "30",
    selectedUserEmail: "",
  });
  const [listPage, setListPage] = useState(1);

  useEffect(() => {
    setListPage(1);
  }, [chartFilters.scope, chartFilters.range, chartFilters.selectedUserEmail]);

  const listUserEmailForQuery = useMemo(() => {
    if (chartFilters.scope === "user") {
      return (chartFilters.selectedUserEmail || user?.email || "").trim();
    }
    return "";
  }, [chartFilters.scope, chartFilters.selectedUserEmail, user?.email]);

  const listQ = useQuery({
    queryKey: [
      "calling",
      "call-logs-list",
      chartFilters.scope,
      listUserEmailForQuery,
      chartFilters.range,
      listPage,
    ],
    queryFn: async () => {
      const q = new URLSearchParams();
      q.set("scope", chartFilters.scope);
      q.set("days", chartFilters.range);
      q.set("limit", String(LIST_PAGE_SIZE));
      q.set("offset", String((listPage - 1) * LIST_PAGE_SIZE));
      if (chartFilters.scope === "user" && listUserEmailForQuery) {
        q.set("userEmail", listUserEmailForQuery);
      }
      const r = await apiFetch(`/api/crm/calling/call-logs?${q.toString()}`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error("call_log_fetch");
      return r.json() as Promise<CallingCallLogsListResponse>;
    },
  });

  const entries = listQ.data?.entries ?? [];
  const hasMore = listQ.data?.hasMore ?? false;

  const patchChartFilters = (patch: Partial<CallLogChartFilterState>) => {
    setChartFilters((f) => ({ ...f, ...patch }));
  };

  const sidebar = (
    <p className="px-1 text-sm text-muted-foreground">{t("calling.callLogPageHint")}</p>
  );

  const showListPagination = listPage > 1 || hasMore;

  return (
    <ContactsHubShell title={t("calling.callLogPageTitle")} activeTab="callLog" sidebar={sidebar}>
      <div className="min-h-0 flex-1 overflow-auto">
        <div className="p-3 sm:p-4">
          <CallLogsDailyChartCard filters={chartFilters} onFiltersChange={patchChartFilters} />
        </div>

        <div className="px-3 sm:px-4 pb-6">
          {listQ.isLoading && listPage === 1 ? (
            <div className="flex justify-center py-12 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : listQ.isError ? (
            <AppEmptyState
              className="py-12"
              title={t("calling.callLogLoadError")}
              description={null}
            />
          ) : listQ.isSuccess && entries.length === 0 && listPage === 1 ? (
            <AppEmptyState className="py-12" title={t("calling.callLogEmpty")} description={null} />
          ) : (
            <>
              <div
                className={cn(
                  "rounded-md border overflow-x-auto",
                  listQ.isFetching && "opacity-70 transition-opacity",
                )}
              >
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">{t("calling.callLogWhen")}</TableHead>
                      <TableHead className="whitespace-nowrap">{t("calling.callLogCalledBy")}</TableHead>
                      <TableHead className="min-w-[140px]">{t("calling.callLogCalledPerson")}</TableHead>
                      <TableHead className="min-w-[160px]">{t("calling.callLogDeal")}</TableHead>
                      <TableHead className="w-[1%] whitespace-nowrap">{t("calling.callLogSurface")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((e) => {
                      const at = e.createdAt ? new Date(e.createdAt) : null;
                      const when =
                        at && !Number.isNaN(at.getTime())
                          ? at.toLocaleString(undefined, {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "—";
                      const surfaceLabel =
                        e.surface === "table"
                          ? t("calling.callLogSurfaceTable")
                          : e.surface === "mobile"
                            ? t("calling.callLogSurfaceMobile")
                            : "—";
                      const dealId = e.dealId;
                      const dealLabel = (e.dealTitle || "").trim() || "—";
                      const contactLabel = (e.contactName || "").trim() || "—";
                      return (
                        <TableRow key={e.id}>
                          <TableCell className="text-muted-foreground text-sm align-top whitespace-nowrap">
                            {when}
                          </TableCell>
                          <TableCell className="text-sm align-top break-all max-w-[200px]">
                            {e.initiatorEmail}
                          </TableCell>
                          <TableCell className="align-top">
                            {e.contactId ? (
                              <Link
                                to={`/contacts/${e.contactId}`}
                                className="text-primary hover:underline break-words"
                              >
                                {contactLabel}
                              </Link>
                            ) : (
                              <span className="text-foreground/80">{contactLabel}</span>
                            )}
                          </TableCell>
                          <TableCell className="align-top min-w-0">
                            {dealId ? (
                              <Link
                                to={callingDealPath(dealId)}
                                className="text-primary hover:underline line-clamp-2"
                                title={dealLabel}
                              >
                                {dealLabel}
                              </Link>
                            ) : (
                              <span className="text-foreground/80 line-clamp-2">{dealLabel}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground align-top whitespace-nowrap">
                            {e.surface ? surfaceLabel : "—"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {showListPagination && (
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={listPage <= 1 || listQ.isFetching}
                    onClick={() => setListPage((p) => Math.max(1, p - 1))}
                  >
                    {t("calling.prev")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!hasMore || listQ.isFetching}
                    onClick={() => setListPage((p) => p + 1)}
                  >
                    {t("calling.next")}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </ContactsHubShell>
  );
}
